import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { ENV } from "./env";

export interface Staff {
  name: string;
  email: string;
  tenantId: string;
}

/** What the app needs from the sign-in: no password, no sign-up, no forgot-password (BR-241). */
export interface AuthClient {
  staff: Staff | null;
  ready: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
}

const Ctx = createContext<AuthClient | null>(null);

export function useAuth(): AuthClient {
  const c = useContext(Ctx);
  if (!c) throw new Error("AuthProvider is missing");
  return c;
}

/** Test mode: the client is injected and nothing talks to Entra. */
export function StaticAuthProvider({ client, children }: { client: AuthClient; children: ReactNode }) {
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

/** Local design with `VITE_MOCK=1`: signing in just sets the persona of the mockups. */
export function MockAuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const client = useMemo<AuthClient>(
    () => ({
      staff,
      ready: true,
      signIn: async () =>
        setStaff({
          name: "Jordan Avery",
          email: "jordan.avery@guestpilot.com",
          tenantId: ENV.tenantId || "guestpilot",
        }),
      signOut: async () => setStaff(null),
      getToken: async () => (staff ? "mock-token" : undefined),
    }),
    [staff],
  );
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

function MsalBridge({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  const scopes = useMemo(() => [ENV.apiScope], []);
  const getToken = useCallback(async () => {
    if (!account) return undefined;
    try {
      return (await instance.acquireTokenSilent({ scopes, account })).accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) await instance.acquireTokenRedirect({ scopes, account });
      return undefined;
    }
  }, [instance, account, scopes]);
  const client = useMemo<AuthClient>(
    () => ({
      staff: account
        ? { name: account.name ?? account.username, email: account.username, tenantId: account.tenantId }
        : null,
      ready: inProgress === "none",
      signIn: async () => {
        await instance.loginRedirect({ scopes, prompt: "select_account" });
      },
      signOut: async () => {
        await instance.logoutRedirect({ account: account ?? undefined });
      },
      getToken,
    }),
    [account, inProgress, instance, scopes, getToken],
  );
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

/** Entra ID against GuestPilot's own single tenant (own app registration, BR-241); the tenant check on the server is the real gate. */
export function MsalAuthProvider({ children }: { children: ReactNode }) {
  const [pca] = useState(
    () =>
      new PublicClientApplication({
        auth: {
          clientId: ENV.clientId,
          authority: `https://login.microsoftonline.com/${ENV.tenantId}`,
          redirectUri: window.location.origin,
        },
        cache: { cacheLocation: "sessionStorage" },
      }),
  );
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void pca
      .initialize()
      .then(() => pca.handleRedirectPromise())
      .then((r) => {
        if (r?.account) pca.setActiveAccount(r.account);
        setReady(true);
      })
      .catch(() => setReady(true));
  }, [pca]);
  if (!ready) return null;
  return (
    <MsalProvider instance={pca}>
      <MsalBridge>{children}</MsalBridge>
    </MsalProvider>
  );
}
